import { APICfg } from 'services/api/api'
import {
  convertEmptyStringToUndefined,
  getId,
  PossiblyEmptyString,
} from 'services/utils'
import { WithIdAndName } from 'types/common'
import { PackageEntity } from '../..'
import { uploadOrKeepImg } from '../../common'
import { APIImage, FormImage } from '../../entities'
import { parseImage } from '../utils'
import { APITailoredProduct, TailoredProduct } from '../types'
import {
  BaseProductPackage,
  OptionedPackage,
  PackageBase,
  ProductPackage,
} from './common'
import { APIDrinkPackage, DrinkPackage } from './drinkPackage'
import { FoodPackage } from './foodPackage'
import { LocationPackage } from './locationPackage'
import kampaayDataProvider from 'services/api/dataProvider'
import { tailoredProductParse } from '../mainProduct'

export type APIEventPackage = PackageBase &
  APITailoredProduct & {
    image: APIImage
    physicalPackage?: OptionedPackage
    foodPackage?: FoodPackage
    locationPackage?: LocationPackage
    drinkPackage?: DrinkPackage
    professionalPackage?: BaseProductPackage
    musicPackage?: BaseProductPackage
    rentPackage?: BaseProductPackage
    sections: WithIdAndName[]
  }

export type EventPackage = PackageBase &
  TailoredProduct &
  PossiblyEmptyString<EventPackagePackages, EventPackagePackageKeys> & {
    image: FormImage
    sections: number[]
  }

export type EventPackagePackages = {
  physicalPackage?: number | OptionedPackage
  foodPackage?: number | FoodPackage
  locationPackage?: number | LocationPackage
  drinkPackage?: number | APIDrinkPackage
  professionalPackage?: number | BaseProductPackage
  musicPackage?: number | BaseProductPackage
  rentPackage?: number | BaseProductPackage
}

export type EventPackagePackageKeys = keyof EventPackagePackages

type EventPackageToAPI = Omit<EventPackage, 'image' | EventPackagePackageKeys> &
  EventPackagePackages & {
    image?: string
  }

const createPakageOrNot = async <T extends ProductPackage>(
  entity: PackageEntity,
  pkg: T | number | undefined | ''
) =>
  typeof pkg === 'object'
    ? (await kampaayDataProvider.create<T>(entity, { data: pkg })).data.id
    : pkg

const apiCfg: APICfg = {
  endpoint: 'eventPackage',
  parse: ({
    customers,
    organizations,
    image,
    ...res
  }: APIEventPackage): EventPackage => ({
    ...res,
    ...tailoredProductParse({ customers, organizations }),
    physicalPackage: res.physicalPackage?.id,
    foodPackage: res.foodPackage?.id,
    locationPackage: res.locationPackage?.id,
    drinkPackage: res.drinkPackage?.id,
    professionalPackage: res.professionalPackage?.id,
    musicPackage: res.musicPackage?.id,
    rentPackage: res.rentPackage?.id,
    sections: res.sections.map(getId),
    image: parseImage(image)!,
  }),
  write: async ({
    rentPackage,
    professionalPackage,
    musicPackage,
    drinkPackage,
    locationPackage,
    foodPackage,
    physicalPackage,
    ...req
  }: EventPackage): Promise<EventPackageToAPI> => {
    const image = await uploadOrKeepImg(req.image)

    const createdRentPackage = await createPakageOrNot(
      'rentpackages',
      rentPackage
    )
    const createdProfessionalPackage = await createPakageOrNot(
      'professionalpackages',
      professionalPackage
    )
    const createdMusicPackage = await createPakageOrNot(
      'musicpackages',
      musicPackage
    )
    const createdDrinkPackage = await createPakageOrNot(
      'drinkpackages',
      drinkPackage
    )
    const createdLocationPackage = await createPakageOrNot(
      'locationpackages',
      locationPackage
    )
    const createdFoodPackage = await createPakageOrNot(
      'foodpackages',
      foodPackage
    )
    const createdPhysicalPackage = await createPakageOrNot(
      'physicalpackages',
      physicalPackage
    )
    return {
      ...req,
      image,
      rentPackage: convertEmptyStringToUndefined(createdRentPackage),
      professionalPackage: convertEmptyStringToUndefined(
        createdProfessionalPackage
      ),
      musicPackage: convertEmptyStringToUndefined(createdMusicPackage),
      drinkPackage: convertEmptyStringToUndefined(createdDrinkPackage),
      locationPackage: convertEmptyStringToUndefined(createdLocationPackage),
      foodPackage: convertEmptyStringToUndefined(createdFoodPackage),
      physicalPackage: convertEmptyStringToUndefined(createdPhysicalPackage),
    }
  },
}

export default apiCfg
